import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { auth, ApiClientError } from '../lib/api'
import { Button } from '../components'

/**
 * Hard auth gate. Until a valid passkey session exists, NOTHING of the app is
 * rendered or fetched — only the sign-in screen. This is the privacy contract:
 * a single user, everything behind a passkey (SPEC §5, security.md).
 */
type GateState = 'checking' | 'unauthed' | 'authed'

export function AuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GateState>('checking')

  const check = useCallback(async () => {
    try {
      await auth.me()
      setState('authed')
    } catch {
      setState('unauthed')
    }
  }, [])

  useEffect(() => {
    void check()
  }, [check])

  if (state === 'checking') {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background text-secondary">
        <p role="status" className="text-body">
          Loading…
        </p>
      </div>
    )
  }

  if (state === 'unauthed') {
    return <AuthScreen onAuthed={() => setState('authed')} />
  }

  return <>{children}</>
}

function AuthScreen({ onAuthed }: { onAuthed: () => void }) {
  const [enrolled, setEnrolled] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    auth
      .status()
      .then((s) => setEnrolled(s.enrolled))
      .catch(() => setEnrolled(true)) // on doubt, show sign-in (never expose enroll blindly)
  }, [])

  const run = async (fn: () => Promise<{ verified: boolean }>) => {
    setBusy(true)
    setError(null)
    try {
      const { verified } = await fn()
      if (verified) onAuthed()
      else setError('Authentication failed.')
    } catch (e: unknown) {
      setError(e instanceof ApiClientError ? e.message : 'Authentication failed.')
    } finally {
      setBusy(false)
    }
  }

  const supported = auth.isSupported()

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-md text-foreground">
      <div className="w-full max-w-[384px] rounded-lg border border-border bg-surface p-lg text-center shadow-card">
        <h1 className="text-h1 font-display text-foreground">Photoshoot</h1>
        <p className="mt-sm text-body text-secondary">
          {enrolled === false
            ? 'Set up your passkey to begin — only you can unlock this.'
            : 'Sign in with your passkey to continue.'}
        </p>

        {!supported && (
          <p role="alert" className="mt-md text-caption text-destructive">
            This browser does not support passkeys.
          </p>
        )}

        <div className="mt-lg">
          {enrolled === false ? (
            <Button
              onClick={() => void run(() => auth.register())}
              disabled={busy || !supported}
            >
              {busy ? 'Creating…' : 'Create passkey'}
            </Button>
          ) : (
            <Button
              onClick={() => void run(() => auth.login())}
              disabled={busy || !supported || enrolled === null}
            >
              {busy ? 'Signing in…' : 'Sign in with passkey'}
            </Button>
          )}
        </div>

        {error && (
          <p role="alert" className="mt-md text-caption text-destructive">
            {error}
          </p>
        )}
      </div>
    </div>
  )
}
